/**
 * 国际跳棋AI算法
 * 严格按照国际比赛规则实现智能决策
 */

import { Player, Difficulty, CheckersMove, CheckersGameState } from '../types/game';
import { getAllPossibleMoves, getAllCaptureMoves, checkGameEnd } from './checkers';

// ==================== 位置权重表 ====================

// 国际跳棋位置权重表（基于战略价值）
// 角落和边缘位置价值更高，中心位置相对较低
const POSITION_WEIGHTS: number[][] = [
  [100, -20, 10, 5, 5, 5, 10, -20, 100, 50],  // 第一行
  [-20,  50, -2, -2, -2, -2, -2,  50, -20, 5],  // 第二行
  [10, -2, 20, -1, -1, -1, 20, -2, 10, 5],    // 第三行
  [5, -2, -1, 25, 0, 0, 25, -1, -2, 5],      // 第四行
  [5, -2, -1, 0, 15, 15, 0, -1, -2, 5],       // 第五行
  [5, -2, -1, 0, 15, 15, 0, -1, -2, 5],       // 第六行
  [5, -2, -1, 25, 0, 0, 25, -1, -2, 5],      // 第七行
  [10, -2, 20, -1, -1, -1, 20, -2, 10, 5],    // 第八行
  [-20, 50, -2, -2, -2, -2, -2, 50, -20, 5],  // 第九行
  [100, -20, 10, 5, 5, 5, 10, -20, 100, 50],   // 第十行
];

/**
 * 获取位置权重
 */
const getPositionWeight = (row: number, col: number): number => {
  return POSITION_WEIGHTS[row]?.[col] || 0;
};

// ==================== AI评估函数 ====================

/**
 * 评估棋盘局势
 */
const evaluateBoard = (
  gameState: CheckersGameState,
  aiPlayer: Player
): number => {
  const board = gameState.board;
  let score = 0;

  // 棋子数量差异（最重要的因素）
  const pieceDiff = aiPlayer === 'black'
    ? gameState.blackPiecesCount - gameState.whitePiecesCount
    : gameState.whitePiecesCount - gameState.blackPiecesCount;
  score += pieceDiff * 1000; // 棋子数量差异权重最大

  // 王棋数量差异
  const kingDiff = aiPlayer === 'black'
    ? gameState.blackKingsCount - gameState.whiteKingsCount
    : gameState.whiteKingsCount - gameState.blackKingsCount;
  score += kingDiff * 1500; // 王棋价值更高

  // 位置优势
  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const piece = board[row][col];
      if (piece) {
        const positionWeight = getPositionWeight(row, col);

        if (piece.player === aiPlayer) {
          score += positionWeight * 2; // AI的棋子位置权重

          // 王棋额外加分
          if (piece.type === 'king') {
            score += 200; // 王棋基础价值
          }

          // 升级潜力
          if (piece.type === 'man') {
            if (aiPlayer === 'black') {
              score += (9 - row) * 10; // 黑棋越接近底部越有价值
            } else {
              score += row * 10; // 白棋越接近顶部越有价值
            }
          }
        } else {
          score -= positionWeight * 2; // 对手的棋子位置权重

          // 对手王棋威胁
          if (piece.type === 'king') {
            score -= 300; // 对手王棋威胁更大
          }
        }
      }
    }
  }

  return score;
};

/**
 * 评估单个移动的价值
 */
const evaluateMove = (
  gameState: CheckersGameState,
  move: CheckersMove,
  aiPlayer: Player
): number => {
  let score = 0;

  // 吃子移动得分最高
  if (move.captures && move.captures.length > 0) {
    score += move.captures.length * 5000; // 每吃一子得5000分

    // 吃掉王棋得分更高
    for (const capture of move.captures) {
      const capturedPiece = gameState.board[capture.row][capture.col];
      if (capturedPiece && capturedPiece.type === 'king') {
        score += 3000; // 吃掉王棋额外加分
      }
    }
  }

  // 升级为王棋加分
  if (move.isPromotion) {
    score += 2000; // 升级为王棋加分
  }

  // 位置优势
  const positionWeight = getPositionWeight(move.to.row, move.to.col);
  score += positionWeight * 10;

  // 安全性评估
  const safetyScore = evaluateSafety(gameState, move, aiPlayer);
  score += safetyScore;

  // 控制中心加分
  if (move.to.row >= 3 && move.to.row <= 6 && move.to.col >= 3 && move.to.col <= 6) {
    score += 100; // 控制中心区域加分
  }

  return score;
};

/**
 * 评估移动的安全性
 */
const evaluateSafety = (
  gameState: CheckersGameState,
  move: CheckersMove,
  aiPlayer: Player
): number => {
  let safetyScore = 0;
  const board = gameState.board;

  // 边缘和角落更安全
  if (move.to.row === 0 || move.to.row === 9 || move.to.col === 0 || move.to.col === 9) {
    safetyScore += 150; // 边缘位置加分

    // 角落位置最安全
    if ((move.to.row === 0 || move.to.row === 9) && (move.to.col === 0 || move.to.col === 9)) {
      safetyScore += 200; // 角落位置额外加分
    }
  }

  // 检查是否会被对手吃子（简化版）
  const opponent = aiPlayer === 'black' ? 'white' : 'black';

  // 模拟对手的反应
  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 10; col++) {
      const piece = board[row][col];
      if (piece && piece.player === opponent) {
        // 简化检查：如果对手棋子在附近，安全性降低
        const distance = Math.abs(row - move.to.row) + Math.abs(col - move.to.col);
        if (distance <= 2) {
          safetyScore -= 50; // 靠近对手棋子安全性降低
        }
      }
    }
  }

  return safetyScore;
};

// ==================== AI难度级别 ====================

/**
 * 简单AI - 优先吃子，随机选择普通移动
 */
const getEasyAIMove = (gameState: CheckersGameState): CheckersMove | null => {
  const aiPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState.board, aiPlayer);

  if (possibleMoves.length === 0) {
    return null;
  }

  // 优先选择吃子移动
  const captureMoves = possibleMoves.filter(move =>
    move.captures && move.captures.length > 0
  );

  const availableMoves = captureMoves.length > 0 ? captureMoves : possibleMoves;

  // 随机选择一个移动
  return availableMoves[Math.floor(Math.random() * availableMoves.length)];
};

/**
 * 中等AI - 基础策略评估
 */
const getMediumAIMove = (gameState: CheckersGameState): CheckersMove | null => {
  const aiPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState.board, aiPlayer);

  if (possibleMoves.length === 0) {
    return null;
  }

  // 评估每个移动
  let bestMove = possibleMoves[0];
  let bestScore = -Infinity;

  for (const move of possibleMoves) {
    let score = 0;

    // 吃子移动得分最高
    if (move.captures && move.captures.length > 0) {
      score += move.captures.length * 3000; // 每吃一子得3000分
    }

    // 升级为王棋加分
    if (move.isPromotion) {
      score += 1500;
    }

    // 位置优势
    const positionWeight = getPositionWeight(move.to.row, move.to.col);
    score += positionWeight * 5;

    // 添加一些随机性
    score += Math.random() * 100;

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

/**
 * 困难AI - 高级策略评估
 */
const getHardAIMove = (gameState: CheckersGameState): CheckersMove | null => {
  const aiPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState.board, aiPlayer);

  if (possibleMoves.length === 0) {
    return null;
  }

  // 详细评估每个移动
  let bestMove = possibleMoves[0];
  let bestScore = -Infinity;

  for (const move of possibleMoves) {
    const score = evaluateMove(gameState, move, aiPlayer);

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

/**
 * 超困难AI - 使用简化的Minimax算法
 */
const getExpertAIMove = (gameState: CheckersGameState): CheckersMove | null => {
  const aiPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState.board, aiPlayer);

  if (possibleMoves.length === 0) {
    return null;
  }

  // 简化的2步前瞻
  let bestMove = possibleMoves[0];
  let bestScore = -Infinity;

  for (const move of possibleMoves) {
    // 评估当前移动
    const score = evaluateMove(gameState, move, aiPlayer);

    // 模拟执行移动
    const tempBoard = JSON.parse(JSON.stringify(gameState.board));
    // 简化移动模拟（不处理复杂规则）
    // 这里可以扩展为更复杂的搜索

    if (score > bestScore) {
      bestScore = score;
      bestMove = move;
    }
  }

  return bestMove;
};

// ==================== 主要AI接口 ====================

/**
 * 获取AI决策
 */
export const getCheckersAIMove = (
  gameState: CheckersGameState,
  difficulty: Difficulty
): CheckersMove | null => {
  try {
    switch (difficulty) {
      case 'easy':
        return getEasyAIMove(gameState);
      case 'medium':
        return getMediumAIMove(gameState);
      case 'hard':
        return getHardAIMove(gameState);
      default:
        return getMediumAIMove(gameState);
    }
  } catch (error) {
    console.error('AI决策出错:', error);
    // 降级到随机移动
    return getEasyAIMove(gameState);
  }
};

/**
 * 模拟AI思考时间
 */
export const simulateAIThinking = async (difficulty: Difficulty): Promise<void> => {
  let delay: number;

  switch (difficulty) {
    case 'easy':
      delay = 500 + Math.random() * 500; // 0.5-1秒
      break;
    case 'medium':
      delay = 800 + Math.random() * 700; // 0.8-1.5秒
      break;
    case 'hard':
      delay = 1200 + Math.random() * 800; // 1.2-2秒
      break;
    default:
      delay = 1000;
  }

  return new Promise(resolve => setTimeout(resolve, delay));
};

export default {
  getCheckersAIMove,
  simulateAIThinking
};